/**
 * Modal para coletar WhatsApp no primeiro login
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import { WhatsApp } from '@mui/icons-material';

interface WhatsAppModalProps {
  open: boolean;
  onSubmit: (whatsapp: string) => void;
  loading?: boolean;
}

const WhatsAppModal: React.FC<WhatsAppModalProps> = ({ open, onSubmit, loading = false }) => {
  const [whatsapp, setWhatsapp] = useState('');
  const [error, setError] = useState('');

  const formatWhatsApp = (value: string) => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');
    
    // Formata para (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
    }
    return numbers.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatWhatsApp(e.target.value);
    setWhatsapp(formatted);
    setError('');
  };

  const handleSubmit = () => {
    const numbersOnly = whatsapp.replace(/\D/g, '');
    
    // Validar
    if (numbersOnly.length < 10 || numbersOnly.length > 11) {
      setError('Digite um número válido com DDD');
      return;
    }

    onSubmit(whatsapp);
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason === 'backdropClick') {
          return; // Não permite fechar clicando fora
        }
      }}
      PaperProps={{
        sx: {
          backgroundColor: '#1a1a1a',
          border: '1px solid rgba(255, 107, 53, 0.3)',
          minWidth: 400,
        }
      }}
    >
      <DialogTitle sx={{ color: '#FFFFFF', textAlign: 'center' }}>
        <WhatsApp sx={{ fontSize: 60, color: '#25D366', mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Número do WhatsApp
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'rgba(255, 255, 255, 0.8)', 
            mb: 3,
            textAlign: 'center',
            lineHeight: 1.6
          }}
        >
          Para confirmar sua identidade e liberar o acesso,
          <br />
          precisamos do seu número de WhatsApp.
          <br />
          <br />
          <strong>Entraremos em contato para aprovação.</strong>
        </Typography>
        
        <TextField
          autoFocus
          label="WhatsApp"
          value={whatsapp}
          onChange={handleChange}
          placeholder="(11) 98765-4321"
          fullWidth
          error={!!error}
          helperText={error || 'Digite com DDD'}
          sx={{
            '& .MuiOutlinedInput-root': {
              color: '#FFFFFF',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
              '&:hover fieldset': { borderColor: 'rgba(255, 107, 53, 0.5)' },
              '&.Mui-focused fieldset': { borderColor: '#FF6B35' },
            },
            '& .MuiInputLabel-root': {
              color: 'rgba(255, 255, 255, 0.7)',
            },
            '& .MuiFormHelperText-root': {
              color: error ? '#f44336' : 'rgba(255, 255, 255, 0.5)',
            },
          }}
        />

        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: 'rgba(255, 107, 53, 0.1)',
            border: '1px solid rgba(255, 107, 53, 0.3)',
            borderRadius: 1,
          }}
        >
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            ℹ️ Seu número será usado apenas para confirmação e não será compartilhado.
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          fullWidth
          disabled={!whatsapp || loading}
          startIcon={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : undefined}
          sx={{
            backgroundColor: '#FF6B35',
            py: 1.5,
            '&:hover': { backgroundColor: '#FF8A5B' },
            '&:disabled': {
              backgroundColor: 'rgba(255, 107, 53, 0.5)',
              color: 'rgba(255, 255, 255, 0.7)',
            }
          }}
        >
          {loading ? 'Salvando...' : 'Enviar e Aguardar Aprovação'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WhatsAppModal;
